import * as React from 'react';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { useTranslation } from 'next-i18next';
import { useAtom } from 'jotai';

import { IconButton, Modal, ModalContent } from '@restworld/ui-ds-base';

import AddCollaboratorFlow from './add-collaborator-flow';
import {
  collaboratorActionModalVariantAtom,
  CollaboratorActionModalVariantType,
  useCollaboratorActionModal,
  collaboratorActionAtom,
} from '../../collaborators.atoms';
import EditCollaboratorFlow from './edit-collaborator-flow';
import RemoveCollaboratorFlow from './remove-collaborator-flow';
import { CollaboratorModalFooterContext } from './collaborator-modal-footer-context';

import { cn } from '@restworld/utils-common';
import type { JSX } from 'react';
function CollaboratorActionModal() {
  const { t } = useTranslation('common');
  const [collaboratorActionModalVariant] = useAtom(collaboratorActionModalVariantAtom);
  const { closeCollaboratorActionModal, isCollaboratorActionModalOpen } = useCollaboratorActionModal();
  const { currentActionEl, currentActionHeaderText } = useCurrentAction();
  const [footer, setFooter] = React.useState<React.ReactNode>(null);

  const handleClose = React.useCallback(() => {
    setFooter(null);
    closeCollaboratorActionModal();
  }, [closeCollaboratorActionModal]);

  const footerContextValue = React.useMemo(() => ({ setFooter }), []);

  return (
    <Modal open={isCollaboratorActionModalOpen} onOpenChange={(v) => !v && handleClose()}>
      {/* md:max-w-2xl (~672px) matches master's rendered width. Master also declared max-w-screen-lg,
          but its legacy ui-ds ModalContent sized to content; the new ui-ds-base ModalContent fills to
          the cap, so screen-lg rendered ~1024px (too wide). md-scoped so the sub-md full-width bottom
          drawer isn't constrained. Shared by all flows (add / edit / remove). */}
      <ModalContent className="md:max-w-2xl" noPadding hideHeader footer={footer}>
        <CollaboratorModalFooterContext.Provider value={footerContextValue}>
          <MainWrapper>
            <HeaderWrapper variant={collaboratorActionModalVariant}>
              {collaboratorActionModalVariant === 'default' && (
                <HeaderContentWrapper>{currentActionHeaderText}</HeaderContentWrapper>
              )}
              <IconButton ariaLabel={t('close')} Icon={XMarkIcon} onClick={handleClose} />
            </HeaderWrapper>
            {currentActionEl}
          </MainWrapper>
        </CollaboratorModalFooterContext.Provider>
      </ModalContent>
    </Modal>
  );
}
export default CollaboratorActionModal;

function useCurrentAction() {
  const { t } = useTranslation('collaborators-page');
  const [collaboratorAction] = useAtom(collaboratorActionAtom);

  let currentActionEl: JSX.Element;
  let currentActionHeaderText: string;

  if (collaboratorAction === 'update') {
    currentActionEl = <EditCollaboratorFlow />;
    currentActionHeaderText = t('edit_permission_level');
  } else if (collaboratorAction === 'delete') {
    currentActionEl = <RemoveCollaboratorFlow />;
  } else {
    currentActionEl = <AddCollaboratorFlow />;
    currentActionHeaderText = t('invite_a_collaborator');
  }

  return { currentActionEl, currentActionHeaderText };
}

const MainWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('w-full max-h-[100vh]', className)} {...props} />
);
const HeaderWrapper = ({
  variant,
  className,
  ...props
}: { variant?: CollaboratorActionModalVariantType } & React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn(
      'hidden w-full items-center py-[10.5px] px-4 md:p-3 md:flex lg:pl-8',
      variant === 'default' ? 'border-b border-neutral-40 justify-between' : 'justify-end',
      className
    )}
    {...props}
  />
);
const HeaderContentWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('', className)} {...props} />
);
